import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  Input,
} from '@angular/core';
import { CompanyCategory, UserCategory } from 'src/app/core/models/core.models';

import { BehaviorSubject, filter, Observable, switchMap, tap } from 'rxjs';
import { PostUserCategoriesEditorService } from 'src/app/feed/services/post-user-categories-editor.service';
import { FormControl, FormGroup } from '@angular/forms';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';
import { CategoryComponent } from '../category/category.component';
import { AsyncPipe } from '@angular/common';

@Component({
  selector: 'app-post-user-categories-list',
  templateUrl: './post-user-categories-list.component.html',
  styleUrls: ['./post-user-categories-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CategoryComponent, AsyncPipe],
})
export class PostUserCategoriesListComponent {
  private destroyRef = inject(DestroyRef);
  formGroup: FormGroup = new FormGroup({});

  // 1) Subject of incoming IDs
  private companyCategoryId$ = new BehaviorSubject<number | null>(null);

  private readonly editor = inject(PostUserCategoriesEditorService);
  private readonly categories$ = toObservable(this.editor.categories);

  // 2) Public stream of the selected CompanyCategory
  readonly companyCategory$: Observable<CompanyCategory | null> =
    this.companyCategoryId$.pipe(
      // drop the initial null
      filter((id): id is number => id !== null),
      // re-read every time the ID changes or the tree is edited
      switchMap((id) =>
        this.categories$.pipe(map(() => this.editor.categoryById(id))),
      ),
    );

  ngOnInit(): void {
    this.companyCategory$
      .pipe(
        tap((category) => {
          this.buildCategoryForm(category);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  // 3) Input setter pushes new IDs into the stream
  @Input() set companyCategoryId(id: number) {
    // console.log('companyCategoryId set to', id);
    this.companyCategoryId$.next(id);
  }

  buildCategoryForm(category: CompanyCategory) {
    const group: Record<string, FormControl<boolean>> = {};

    const addCategory = (cat: UserCategory) => {
      group[cat.id] = new FormControl(cat.is_selected);

      cat.children?.forEach(addCategory);
    };

    category.categories.forEach(addCategory);

    return new FormGroup(group);
  }
}
